// TimePicker — lazy component, creates popup on first focus

export function initTimePickers() {
	// Event delegation — one-time setup
	if (document._timePickerInit) return;
	document._timePickerInit = true;

	let isClickingButton = false;

	// Focus on hour/min input → open popup
	document.addEventListener('focusin', e => {
		const input = e.target.closest('.timepicker__input');
		if (!input) return;

		const picker = input.closest('.timepicker');
		if (!picker) return;

		// Lazy create popup
		if (!picker.querySelector('.timepicker__popup')) {
			_createPopup(picker);
		}

		const popup = picker.querySelector('.timepicker__popup');
		popup.classList.add('active');

		setTimeout(() => {
			input.select();
			_updateActive(picker);
			_scrollToSelected(picker);
		}, 0);
	});

	// Input — restrict to digits, auto-advance
	document.addEventListener('input', e => {
		const input = e.target.closest('.timepicker__input');
		if (!input) return;

		const picker = input.closest('.timepicker');
		const isHour = input.classList.contains('timepicker__input--hour');
		const max = isHour ? 23 : 59;

		input.value = input.value.replace(/\D/g, '').slice(0, 2);

		if (input.value.length === 2) {
			_correctValue(input, max);
			_updateActive(picker);

			if (isHour) {
				const minInput = picker.querySelector('.timepicker__input--min');
				if (minInput) minInput.focus();
			} else {
				_hidePopup(picker);
				input.blur();
			}
		}
	});

	// Blur — close and trigger change
	document.addEventListener('focusout', e => {
		const input = e.target.closest('.timepicker__input');
		if (!input) return;

		const picker = input.closest('.timepicker');
		const isHour = input.classList.contains('timepicker__input--hour');
		_correctValue(input, isHour ? 23 : 59);
		_updateActive(picker);

		setTimeout(() => {
			if (isClickingButton) return;
			const hourInput = picker.querySelector('.timepicker__input--hour');
			const minInput = picker.querySelector('.timepicker__input--min');
			if (!hourInput.matches(':focus') && !minInput.matches(':focus')) {
				_hidePopup(picker);
			}
		}, 200);
	});

	// Click on hour button
	document.addEventListener('mousedown', e => {
		if (e.target.closest('.timepicker__btn--hour')) isClickingButton = true;
	});

	document.addEventListener('click', e => {
		const btn = e.target.closest('.timepicker__btn--hour');
		if (btn) {
			const picker = btn.closest('.timepicker');
			const hourInput = picker.querySelector('.timepicker__input--hour');
			hourInput.value = btn.textContent.trim().padStart(2, '0');
			_updateActive(picker);
			const minInput = picker.querySelector('.timepicker__input--min');
			if (minInput) minInput.focus();
			isClickingButton = false;
			return;
		}

		const minBtn = e.target.closest('.timepicker__btn--min');
		if (minBtn) {
			const picker = minBtn.closest('.timepicker');
			const minInput = picker.querySelector('.timepicker__input--min');
			const hourInput = picker.querySelector('.timepicker__input--hour');
			minInput.value = minBtn.textContent.trim().padStart(2, '0');
			_updateActive(picker);
			if (!hourInput.value) {
				hourInput.focus();
			} else {
				_hidePopup(picker);
				minInput.blur();
			}
			return;
		}

		if (!e.target.closest('.timepicker__btn--hour')) {
			isClickingButton = false;
		}
	});

	function _createPopup(picker) {
		const popup = document.createElement('div');
		popup.className = 'timepicker__popup';

		let html = '<div class="timepicker__popup-container">';

		// Hours column
		html += '<div class="timepicker__choice"><ul class="timepicker__choice-list">';
		for (let i = 0; i < 24; i++) {
			const v = String(i).padStart(2, '0');
			html += `<li><button class="btn btn--s timepicker__btn timepicker__btn--hour" type="button"><span>${v}</span></button></li>`;
		}
		html += '</ul></div>';

		// Minutes column (5-min increments)
		html += '<div class="timepicker__choice"><ul class="timepicker__choice-list">';
		for (let i = 0; i < 60; i += 5) {
			const v = String(i).padStart(2, '0');
			html += `<li><button class="btn btn--s timepicker__btn timepicker__btn--min" type="button"><span>${v}</span></button></li>`;
		}
		html += '</ul></div>';

		html += '</div>';
		popup.innerHTML = html;
		picker.appendChild(popup);
	}

	function _correctValue(input, max) {
		let v = parseInt(input.value, 10);
		if (isNaN(v)) v = 0;
		v = Math.max(0, Math.min(v, max));
		input.value = String(v).padStart(2, '0');
	}

	function _updateActive(picker) {
		const hourInput = picker.querySelector('.timepicker__input--hour');
		const minInput = picker.querySelector('.timepicker__input--min');
		const popup = picker.querySelector('.timepicker__popup');
		if (!popup) return;

		popup.querySelectorAll('.timepicker__btn--hour').forEach(btn => {
			const val = btn.textContent.trim();
			btn.classList.toggle('active', hourInput.value && val === hourInput.value.padStart(2, '0'));
		});

		popup.querySelectorAll('.timepicker__btn--min').forEach(btn => {
			const val = btn.textContent.trim();
			btn.classList.toggle('active', minInput.value && val === minInput.value.padStart(2, '0'));
		});
	}

	function _scrollToSelected(picker) {
		const popup = picker.querySelector('.timepicker__popup');
		if (!popup) return;

		popup.querySelectorAll('.timepicker__btn.active').forEach(btn => {
			const list = btn.closest('.timepicker__choice-list');
			if (list) {
				const listRect = list.getBoundingClientRect();
				const btnRect = btn.getBoundingClientRect();
				list.scrollTop = btnRect.top - listRect.top + list.scrollTop - (listRect.height / 2) + (btnRect.height / 2);
			}
		});
	}

	function _hidePopup(picker) {
		const popup = picker.querySelector('.timepicker__popup');
		if (!popup) return;

		const wasActive = popup.classList.contains('active');
		popup.classList.remove('active');

		if (wasActive) {
			// Trigger change on hidden value input
			const hiddenInput = picker.querySelector('.timepicker__value');
			const hourInput = picker.querySelector('.timepicker__input--hour');
			const minInput = picker.querySelector('.timepicker__input--min');

			if (hiddenInput && hourInput.value && minInput.value) {
				const newVal = hourInput.value + ':' + minInput.value;
				if (hiddenInput.value !== newVal) {
					hiddenInput.value = newVal;
					hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}
		}
	}
}
